import { inject, awaitRedraw } from "@dhx/ts-common/dom";
import { ICol, IRendererConfig, GridEvents, IEditor, IRow } from "../../types";
import { Combobox } from "@dhx/ts-combobox";
import { focusManager } from "@dhx/ts-common/FocusManager";

export class ComboboxEditor implements IEditor {
	protected _handlers: { [key: string]: (...args) => void };
	protected _cell: { row: IRow; col: ICol };
	protected _config: IRendererConfig;
	protected _input: Combobox;

	constructor(row: any, col: ICol, config: IRendererConfig) {
		this._config = config;
		this._cell = { row, col };
		this._initHandlers();
	}

	endEdit(withoutSave?: boolean) {
		let value: string | number;
		if (!withoutSave) {
			const val = this._input.getValue();
			value = this._cell.col.editorType === "multiselect" ? val.split(",").join(", ") : val;
		}
		if (this._config.events.fire(GridEvents.beforeEditEnd, [value, this._cell.row, this._cell.col])) {
			this._input.popup.hide();
			document.removeEventListener("mousedown", this._handlers.onOuterClick);
			this._cell.row = this._config.datacollection.getItem(this._cell.row.id);
			focusManager.setFocusId(this._config.gridId);
			value
				?.toString()
				.split(", ")
				.forEach(val => {
					const item = this._cell.col.options.find(option => {
						return typeof option === "string" ? option === val : option.id.toString() === val;
					});
					if (val && !item) this._cell.col.options.push(this._input.data.getItem(val));
				});
			this._config.events.fire(GridEvents.afterEditEnd, [value, this._cell.row, this._cell.col]);
		} else {
			this._input.focus();
		}
	}

	toHTML() {
		const content =
			this._cell.col.options.map(item => {
				return typeof item === "string" ? { id: `${item}`, value: item } : item;
			}) || [];

		if (!this._input) {
			this._input = new Combobox(null, {
				itemHeight: 37,
				css: "dhx_cell-editor__combobox",
				multiselection: this._cell.col.editorType === "multiselect",
				...this._cell.col.editorConfig,
			});
			this._input.data.parse(content);
			const comboValue = this._cell.row[this._cell.col.id];
			const value =
				this._cell.col.editorType === "multiselect"
					? (comboValue?.toString() || "").split(", ")
					: comboValue;
			this._input.setValue(value);
			this._input.events.on("keydown", this._handlers.onkeydown);
		}
		document.addEventListener("mousedown", this._handlers.onOuterClick);

		this._config.$editable.editor = this;
		awaitRedraw().then(() => {
			const holderNode = this._input.getRootView().refs.holder.el;
			this._input.popup.getContainer().style.width = holderNode.offsetWidth + "px";
			this._input.popup.show(holderNode);
		});
		focusManager.setFocusId((this._input as any)._uid);
		return inject(this._input.getRootView());
	}

	protected _initHandlers() {
		this._handlers = {
			onOuterClick: (e: Event) => {
				if (e.target instanceof Node) {
					const isInput =
						this._input &&
						this._input.getRootNode() &&
						this._input.getRootNode().contains(e.target);
					const isPopup =
						this._input.popup &&
						this._input.popup.getRootNode() &&
						this._input.popup.getRootNode().contains(e.target);
					if (!(isInput || isPopup)) {
						this.endEdit();
					}
				}
			},
			onkeydown: (e: KeyboardEvent) => {
				if (e.key === "Escape" || e.key === "Tab") {
					this.endEdit();
				}
			},
		};
	}
}
